import React, { ReactNode } from 'react';

const repeat = (count: number): number[] => Array.from({ length: count }, (_, index) => index);

const Bone: React.FC<{ className?: string }> = ({ className }) => (
  <span className={['wealth-skeleton__bone', className].filter(Boolean).join(' ')} />
);

const NetWorthCardSkeleton: React.FC = () => (
  <div className="wealth-skeleton__card wealth-skeleton__net-worth">
    <div className="wealth-skeleton__net-worth-head">
      <div className="wealth-skeleton__stack">
        <Bone className="wealth-skeleton__label" />
        <Bone className="wealth-skeleton__net-worth-value" />
        <Bone className="wealth-skeleton__breakdown" />
      </div>
      <div className="wealth-skeleton__time-range">
        {repeat(6).map((index) => (
          <Bone className="wealth-skeleton__time-option" key={index} />
        ))}
      </div>
    </div>
    <div className="wealth-skeleton__tabs">
      {repeat(3).map((index) => (
        <Bone className="wealth-skeleton__tab" key={index} />
      ))}
    </div>
    <Bone className="wealth-skeleton__chart" />
  </div>
);

const WavvestAccountsSkeleton: React.FC = () => (
  <div className="wealth-skeleton__card wealth-skeleton__table">
    <div className="wealth-skeleton__stack">
      <Bone className="wealth-skeleton__table-title" />
      <Bone className="wealth-skeleton__table-subtitle" />
    </div>
    <div className="wealth-skeleton__wavvest-row wealth-skeleton__columns">
      {repeat(5).map((index) => (
        <Bone className="wealth-skeleton__column" key={index} />
      ))}
    </div>
    {repeat(3).map((index) => (
      <div className="wealth-skeleton__wavvest-row wealth-skeleton__row" key={index}>
        <div className="wealth-skeleton__name">
          <Bone className="wealth-skeleton__mark" />
          <div className="wealth-skeleton__stack">
            <Bone className="wealth-skeleton__line wealth-skeleton__line--title" />
            <Bone className="wealth-skeleton__line wealth-skeleton__line--meta" />
          </div>
        </div>
        <Bone className="wealth-skeleton__pill" />
        <Bone className="wealth-skeleton__line wealth-skeleton__line--amount" />
        <Bone className="wealth-skeleton__pill wealth-skeleton__pill--funding" />
        <Bone className="wealth-skeleton__chevron" />
      </div>
    ))}
  </div>
);

const LinkedAccountsSkeleton: React.FC = () => (
  <div className="wealth-skeleton__card wealth-skeleton__table">
    <div className="wealth-skeleton__table-head">
      <div className="wealth-skeleton__stack">
        <Bone className="wealth-skeleton__table-title" />
        <Bone className="wealth-skeleton__table-subtitle" />
      </div>
      <Bone className="wealth-skeleton__line wealth-skeleton__line--amount" />
    </div>
    {repeat(3).map((index) => (
      <div className="wealth-skeleton__linked-row wealth-skeleton__row" key={index}>
        <div className="wealth-skeleton__name">
          <Bone className="wealth-skeleton__mark" />
          <Bone className="wealth-skeleton__line wealth-skeleton__line--title" />
        </div>
        <Bone className="wealth-skeleton__line wealth-skeleton__line--meta" />
        <Bone className="wealth-skeleton__line wealth-skeleton__line--amount" />
        <Bone className="wealth-skeleton__line wealth-skeleton__line--status" />
      </div>
    ))}
  </div>
);

export type WealthScreenSkeletonProps = {
  /** Draw the header row (profile risk chip) above the net worth card. */
  showHeader?: boolean;
  /** Optional visible status line, e.g. while external accounts are being prepared. */
  message?: ReactNode;
  className?: string;
  /** Render without the live region, for use inside another loading scaffold. */
  decorative?: boolean;
};

const WealthScreenSkeletonBody: React.FC<{ showHeader?: boolean }> = ({ showHeader = true }) => (
  <div className="wealth-skeleton__body">
    {showHeader ? (
      <div className="wealth-skeleton__top">
        <Bone className="wealth-skeleton__risk-chip" />
      </div>
    ) : null}
    <NetWorthCardSkeleton />
    <WavvestAccountsSkeleton />
    <LinkedAccountsSkeleton />
  </div>
);

const WealthScreenSkeleton: React.FC<WealthScreenSkeletonProps> = ({
  showHeader = true,
  message,
  className,
  decorative = false,
}) => {
  const classes = ['wealth-skeleton', className].filter(Boolean).join(' ');
  if (decorative) {
    return (
      <div className={classes} aria-hidden="true">
        <WealthScreenSkeletonBody showHeader={showHeader} />
      </div>
    );
  }
  return (
    <section
      className={classes}
      role="status"
      aria-busy="true"
      aria-label="Loading wealth dashboard"
      aria-live="polite"
    >
      {message ? <p className="wealth-skeleton__message">{message}</p> : null}
      <WealthScreenSkeletonBody showHeader={showHeader} />
    </section>
  );
};

export default WealthScreenSkeleton;
